"use client";

import { forwardRef, useEffect, useLayoutEffect, useState, type RefObject } from "react";
import type { ScrollerProps, TableProps } from "react-virtuoso";
import { ArrowUpRight, Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import { SportsbookLogo } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { trimLine } from "@/components/odds-screen/live/labels";
import type { DisplayPlayerProp, PlatformType } from "@/components/optimizer/utils-v2";
import type { SortDirection } from "@/components/optimizer/query-state";
import { PLATFORM_TO_LOGO_CODE } from "@/components/optimizer/desktop-optimizer-table.columns";
import { decimalToAmerican, type OddsFormat } from "@/utils/calculators/odds-utils";
import { edgeLabel } from "@/lib/whale-watch/whale-watch-shared";
import { trackPropBetLink } from "./fantasy-expansion";
import { formatOdds } from "./model";
import { PromoTag } from "./promo";
import { StakeLabel, StakeTip, stakeSpoken } from "@/components/odds/stake-tip";
import type { StakeQuote } from "@/lib/book-stake";
import { WhalesCell, WhalesHead } from "@/components/odds/whales";
import { rowWhalesSpec } from "@/components/optimizer/utils-v2/displayed-odds";
import type { BoardColumn } from "./model";

export const PLAYER_W = 328;
export const PLAYER_W_PHONE = 236;
export const PLAYER_W_MIN = 168;
export const BOOK_W = 100;
export const BOOK_W_COMPACT = 80;
export const HIT_W = 96;
export const COL_W = 64;
export const WHALES_W = 88;
export const PINNED_SHADOW = "shadow-[1px_0_0_rgba(255,255,255,0.08)]";
export const HEAD = "border-b border-surface-line bg-surface-chrome align-middle";
export const CELL = "border-b border-white/[0.06]";
export const PINNED_HEAD = "sticky z-[30]";
export const PINNED = "sticky z-[1] bg-surface-raised group-aria-expanded:bg-[linear-gradient(rgba(255,255,255,0.1),rgba(255,255,255,0.1))]";
export const ROW_ACTION = "flex h-4 w-4 items-center justify-center rounded text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

const START_COLUMN = "game_start_timestamp";

export type SizedContext = { readonly tableWidth: number; readonly viewportWidth: number };

export type BoardLayout = { readonly playerW: number; readonly bookW: number; readonly pinStats: boolean };

export type BoardLayoutOptions = { readonly playerW?: number; readonly bookW?: number; readonly bookWCompact?: number };

export function boardLayout(width: number, statsW: number, leadStatW: number, columns: readonly BoardColumn[], { playerW: widePlayerW = PLAYER_W, bookW = BOOK_W, bookWCompact = BOOK_W_COMPACT }: BoardLayoutOptions = {}): BoardLayout {
  const tailW = columnsWidth(columns.slice(0, 2));
  if (width === 0 || widePlayerW + bookW + statsW + tailW <= width) return { playerW: widePlayerW, bookW, pinStats: true };
  const playerW = Math.min(PLAYER_W_PHONE, width - tailW, Math.max(PLAYER_W_MIN, width - bookWCompact - leadStatW));
  return { playerW, bookW: bookWCompact, pinStats: playerW + bookWCompact + statsW + tailW <= width };
}

export function useClientWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const host = ref.current;
    if (host === null) return;
    const update = () => setWidth((host.querySelector<HTMLElement>("[data-virtuoso-scroller]") ?? host).clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(host);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

export const Scroller = forwardRef<HTMLDivElement, ScrollerProps & { context?: unknown }>(function Scroller({ context: _context, style, ...props }, ref) {
  return <div ref={ref} {...props} className="scrollbar-custom scrollbar-color-primary [scrollbar-gutter:stable]" style={{ ...style, height: "100%", overflow: "auto" }} />;
});

export function TableEl({ context, style, ...props }: TableProps & { context?: SizedContext }) {
  const width = Math.max(context?.tableWidth ?? 0, context?.viewportWidth ?? 0);
  return <table {...props} style={{ ...style, width, tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }} />;
}

const BADGE_SURFACE = { "--color-surface-elevated": "var(--color-surface-overlay)" } as React.CSSProperties;

export function evText(ev: number): string {
  return edgeLabel(ev).text;
}

export function evPillClass(ev: number): string {
  return valuePillClass(edgeLabel(ev).tone === "value");
}

export function fairText(hitPct: number | null, format: OddsFormat): string | null {
  if (hitPct === null || hitPct <= 0 || hitPct >= 100) return null;
  return `fair ${formatOdds(decimalToAmerican(100 / hitPct), format)}`;
}

export function valuePillClass(positive: boolean): string {
  return cn("data-num inline-flex h-7 items-center rounded-md px-2 text-[13px] font-semibold tabular-nums", positive ? "bg-accent-value/15 text-accent-value" : "bg-white/[0.04] text-zinc-400");
}

export function EvPill({ ev }: { ev: number }) {
  return <span className={evPillClass(ev)}>{evText(ev)}</span>;
}

function hitTone(pct: number, breakeven: number | null): string {
  if (!Number.isFinite(pct) || breakeven === null || !Number.isFinite(breakeven) || breakeven <= 0) return "bg-white/[0.04] text-zinc-400";
  if (pct >= breakeven) return "bg-accent-value/15 text-accent-value";
  if (pct >= breakeven - 1) return "bg-warning/15 text-warning";
  return "bg-destructive/15 text-destructive";
}

export function hitPillClass(pct: number, breakeven: number | null): string {
  return cn("data-num inline-flex h-7 flex-shrink-0 items-center rounded-md px-2 text-[13px] font-semibold tabular-nums transition-colors duration-200 ease-out motion-reduce:transition-none", hitTone(pct, breakeven));
}

export function HitStat({ hit, format }: { hit: number | null; format: OddsFormat }) {
  const fair = fairText(hit, format);
  return (
    <div className="flex flex-col items-start justify-center px-3">
      {hit === null ? (
        <span className="text-[13px] text-zinc-600">–</span>
      ) : (
        <span className="data-num text-[13px] font-semibold tabular-nums text-foreground">{hit.toFixed(1)}%</span>
      )}
      {fair !== null && <span className="data-num whitespace-nowrap text-[10px] leading-4 text-zinc-500">{fair}</span>}
    </div>
  );
}

export function BookHead({ book }: { book: string }) {
  return (
    <div className="flex w-full justify-center py-0.5" style={BADGE_SURFACE} title={sportsbookIdToDisplay(book)}>
      <SportsbookLogo name={PLATFORM_TO_LOGO_CODE[book] ?? book.toUpperCase()} size="xs" />
    </div>
  );
}

export function ColumnHead({ column, tap }: { column: BoardColumn; tap: boolean }) {
  return column.kind === "whales" ? <WhalesHead tap={tap} /> : <BookHead book={column.book} />;
}

export function columnWidth(column: BoardColumn): number {
  return column.kind === "whales" ? WHALES_W : COL_W;
}

export function columnsWidth(columns: readonly BoardColumn[]): number {
  return columns.reduce((sum, column) => sum + columnWidth(column), 0);
}

export function columnKey(column: BoardColumn): string {
  return column.kind === "whales" ? "whales" : column.book;
}

export function WhalesColumnCell({ row, format, isMobile }: { row: DisplayPlayerProp; format: OddsFormat; isMobile: boolean }) {
  return <WhalesCell spec={rowWhalesSpec(row, "side")} format={format} tap={isMobile} className="w-full" />;
}

export function CopyName({ name }: { name: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      aria-label={copied ? `Copied ${name}` : `Copy ${name}`}
      title="Copy name"
      onClick={(event) => {
        event.stopPropagation();
        void navigator.clipboard.writeText(name).then(() => setCopied(true)).catch(() => {});
      }}
      className={cn(
        "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded text-zinc-600 transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        copied ? "text-primary opacity-100" : "opacity-0 group-hover:opacity-100",
      )}
    >
      {copied ? <Check size={11} strokeWidth={2.5} aria-hidden="true" /> : <Copy size={11} strokeWidth={2} aria-hidden="true" />}
    </button>
  );
}

export function PriceCell({
  row, book, odds, line, format, link, primary, better, stake = null, note = null, mult,
}: {
  row: DisplayPlayerProp;
  book: PlatformType;
  odds: number | null;
  line: number | null;
  format: OddsFormat;
  link: string | null;
  primary?: boolean;
  better?: boolean;
  stake?: StakeQuote | null;
  note?: string | null;
  mult?: string | null;
}) {
  const name = sportsbookIdToDisplay(book);
  if (odds === null) {
    return <div className="h-9" aria-label={`${name} not offered`} />;
  }
  const text = formatOdds(odds, format);
  const at = line === null ? null : trimLine(line);
  const shown = note === null ? stake : null;
  const className = cn(
    "flex min-h-9 flex-col items-center justify-center gap-1 rounded-md px-1 py-1",
    link !== null && "hover:bg-white/[0.06]",
    (link !== null || shown !== null) && "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
  );
  const body = (
    <>
      <span className={cn("data-num flex items-center gap-1 text-[13px]", primary ? "font-semibold text-foreground" : better ? "font-semibold text-accent-value-strong" : "font-medium text-foreground/70")}>
        {text}
        {mult != null && <PromoTag>{mult}</PromoTag>}
        {primary && link !== null && <ArrowUpRight size={10} strokeWidth={2.5} className="text-primary/60" aria-hidden="true" />}
      </span>
      {at !== null && (
        <span className="data-num text-[10px] font-medium leading-none tabular-nums text-foreground/70">{at}</span>
      )}
      {note !== null && (
        <span className="data-num text-[10px] font-medium leading-none tabular-nums text-zinc-500" aria-hidden="true">{note}</span>
      )}
      {shown !== null && <StakeLabel stake={shown.stake} className="leading-none" />}
    </>
  );
  const label = `${text}${at ? ` at ${at}` : ""}`;
  const spoken = note === null ? stakeSpoken(shown?.stake ?? null) : `, ${note}`;
  return (
    <StakeTip quote={shown} format={format}>
      {link === null ? (
        <div className={className} tabIndex={shown === null ? undefined : 0} aria-label={`${name} ${label}${spoken}`}>{body}</div>
      ) : (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Bet ${label} at ${name}${spoken}`}
          onClick={(event) => {
            event.stopPropagation();
            trackPropBetLink(row, book, primary ? "cta" : "peer_grid");
          }}
          className={className}
        >
          {body}
        </a>
      )}
    </StakeTip>
  );
}

type SortProps = {
  sort: { readonly column: string; readonly direction: SortDirection };
  onSort: (column: string, direction: SortDirection) => void;
};

function ariaSort(sort: SortProps["sort"], column: string): "ascending" | "descending" | "none" {
  return sort.column !== column ? "none" : sort.direction === "asc" ? "ascending" : "descending";
}

function SortButton({
  column, label, spoken = label, first = "desc", sort, onSort, className, children,
}: SortProps & {
  column: string;
  label: string;
  spoken?: string;
  first?: SortDirection;
  className?: string;
  children?: React.ReactNode;
}) {
  const sorted = sort.column === column;
  const state = ariaSort(sort, column);
  return (
    <button
      type="button"
      onClick={() => onSort(column, sorted ? (sort.direction === "desc" ? "asc" : "desc") : first)}
      aria-label={`Sort by ${spoken.toLowerCase()}, ${state === "none" ? "not sorted" : state}`}
      className={cn("hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", className)}
    >
      <span className={cn("flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.06em]", sorted ? "text-foreground" : "text-zinc-500")}>
        {label}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cn("transition-transform", sorted && sort.direction === "asc" && "rotate-180", !sorted && "opacity-40")}><path d="m6 9 6 6 6-6" /></svg>
      </span>
      {children}
    </button>
  );
}

export function SortHead({
  column, label, sub, sort, onSort, className, style, align = "start",
}: SortProps & {
  column: string;
  label: string;
  sub: string | null;
  className?: string;
  style?: React.CSSProperties;
  align?: "start" | "end";
}) {
  return (
    <th aria-sort={ariaSort(sort, column)} className={cn("text-left", HEAD, className)} style={style}>
      <SortButton column={column} label={label} sort={sort} onSort={onSort} className={cn("flex h-full w-full flex-col justify-center px-3", align === "end" ? "items-end" : "items-start")}>
        {sub !== null && <span className="data-num text-[10px] text-zinc-500">{sub}</span>}
      </SortButton>
    </th>
  );
}

export function PlayHead({ width, column = START_COLUMN, sort, onSort, className }: SortProps & { width: number; column?: string; className?: string }) {
  return (
    <th aria-sort={ariaSort(sort, column)} className={cn("sticky left-0 z-[30] text-left", HEAD, className)} style={{ width }}>
      <div className="flex items-center justify-between pl-4 pr-1.5">
        <span className="text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Play</span>
        <SortButton column={column} label="Start" spoken="start time" first="asc" sort={sort} onSort={onSort} className="flex h-8 items-center rounded-md px-1.5" />
      </div>
    </th>
  );
}
